import { additionalPalette, theme } from '@focaldata/cin-ui-components'
import { useTheme } from '@mui/material'
import React, { memo, useMemo } from 'react'
import ReactWordcloud, { Optional, Options } from 'react-wordcloud'
import { Word, WordCloudChartData } from '../Chart.model'

export interface Props extends React.HTMLAttributes<HTMLElement> {
  data: WordCloudChartData
}

const createTooltip = (word: string | null, value?: number) => {
  const tooltipEl = document.createElement('div')

  Object.assign(tooltipEl.style, {
    'background-color': 'rgba(0,0,0,0.8)',
    'border-radius': '6px',
    padding: '6px',
    opacity: 1,
    'font-family': theme.typography.fontFamily
  })

  const span = document.createElement('span')

  Object.assign(span.style, {
    color: '#fff'
  })
  span.append(`${word} (${value})`)
  tooltipEl.append(span)

  return tooltipEl
}

const WordCloudChart: React.FC<Props> = (props: Props) => {
  const { data }: Props = props
  const theme = useTheme()

  const wordCloudOptions = useMemo<Optional<Options>>(
    () => ({
      fontFamily: theme.typography.fontFamily,
      fontSizes: [12, 64],
      colors: [
        additionalPalette.liveBlue.main,
        additionalPalette.darkBlue.main
      ],
      rotations: 0,
      padding: 5,
      deterministic: true
    }),
    [theme.typography.fontFamily]
  )

  return (
    <ReactWordcloud
      callbacks={wordCloudCallbacks}
      options={wordCloudOptions}
      words={data.words}
    />
  )
}

const wordCloudCallbacks = {
  getWordTooltip: (word: Word) => createTooltip(word.text, word.value)
}

// react-wordcloud merges its options on every render, triggering a new d3
// layout even if props are referentially stable. Stop equivalent data at this
// boundary, including fresh arrays returned by a results refresh.
export default memo(WordCloudChart, (previous, next) => {
  const previousWords = previous.data.words
  const nextWords = next.data.words
  return (
    previousWords === nextWords ||
    (previousWords.length === nextWords.length &&
      previousWords.every(
        (word, index) =>
          word.text === nextWords[index].text &&
          word.value === nextWords[index].value
      ))
  )
})
